// @ts-check
"use strict"

import React, { useId, useRef, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { PatientAutoComplete } from "@minohealth/common/components/patients-autocomplete";
import AutoResizingTextarea from "@minohealth/common/components/AutoResizingTextarea";
import useUserAccounts from "@minohealth/common/hooks/useUserAccounts";
import { useSelector } from "react-redux";
import { showErrorMessage } from "@minohealth/common/utils/notifiers";
import { fieldValueToMarkdown, markdownToFieldValue } from "./utils";
import { capitalise } from "@minohealth/common/utils/string-formatters";

import { FaPlus } from "react-icons/fa";
import { MdOutlineDeleteForever } from "react-icons/md";

import "./edit-report.css"
import "./chat-report-forms.css"
import { useChatContext } from "@/services/chat-provider";

/**
 * @param {Object} props
 * @param {string} props.reportId
 * @param {import(".").ReportData} props.reportData
 * @param {() => void} props.gotoPreview
 */
export default function EditReport({ reportId, reportData, gotoPreview }) {
  const { useActiveChatSession, useUpdateReport } = useChatContext()

  const formId = useId()
  const formDialogRef = useRef(null)

  /** @param {string} id */
  function getScopedId(id) { return `${formId}-${id}` }

  const [dynamicFields, setDynamicFields] = useState([]);

  useEffect(() => {
    if (reportData?.content) {
      const fields = reportData.content.map(field => {
        try {
          return {...field, value: fieldValueToMarkdown(field.value, null)}
        } catch(err) {
          Sentry.captureException(err, {
            extra: {
              context: 'EditReport - fieldValueToMarkdown conversion',
              reportId: reportId || 'undefined',
              fieldLabel: field?.label || 'undefined',
              fieldValueType: typeof field?.value,
              userId: user?._id || 'undefined'
            }
          });
          return { ...field, value: null}
        }
      });
      setDynamicFields(fields);
    }
  }, [reportData?.content]);

  const { user } = useSelector(state => state.app.datastate)
  const { accounts } = useUserAccounts()
  const userFacility = accounts?.find(account => (
    (account._id !== user._id) && account.members != null
  ))

  const { data: activeChatSession } = useActiveChatSession()
  const { mutate: updateReport, isPending: isUpdatingReport } = useUpdateReport()

  function addField(label) {
    mixpanel.track("Add Report Field", {
      fieldLabel: label,
      reportId: reportId
    })
    setDynamicFields(fields => {
      return [...fields, { label: capitalise(label), value: "" }]
    })
  }

  function removeField(label) {
    mixpanel.track("Remove Report Field", {
      fieldLabel: label,
      reportId: reportId
    })
    setDynamicFields(fields => {
      return fields.filter(field => field.label !== label)
    })
  }

  /** @param {React.FormEvent<HTMLFormElement>} ev */
  function handleAddField(ev) {
    ev.preventDefault()
    const formData = new FormData(ev.target)
    addField(formData.get("field"))
    ev.target.reset()
  }

  /** @param {React.FormEvent<HTMLFormElement>} ev */
  async function handleSubmit(ev) {
    ev.preventDefault()

    if(isUpdatingReport) return

    const formData = new FormData(ev.target)

    let newReportData = {
      patient: {
        name: [
          formData.get("patient-first-name"),
          formData.get("patient-last-name")
        ].join(" ").split(" ").map(capitalise).join(" "),
        id: formData.get("patient-id").toUpperCase(),
        age: Number(formData.get("patient-age")),
        gender: formData.get("patient-gender"),
      },
      facility: {
        name: formData.get("facility-name").split(" ").map(capitalise).join(" "),
        reportingDoctor: formData.get("facility-reporting-doctor").split(" ").map(capitalise).join(" "),
        reportDate: formData.get("report-date"),
      },
      date: new Date(formData.get("report-date")).toISOString(),
      content: [],
    }

    mixpanel.track("Submit Report Edit", {
      reportId: reportId,
      patientName: newReportData.patient.name,
      patientAge: newReportData.patient.age,
      facilityName: newReportData.facility.name,
      fieldsCount: dynamicFields.length
    })

    for (let [key, value] of formData.entries()) {
      if (key.startsWith("patient-") || key.startsWith("facility-") || key.startsWith("report-")) {
        continue;
      }
      newReportData.content.push({label: key, value: capitalise(value)});
    }

    if(activeChatSession?._id == null) return

    let patientInfo = newReportData?.patient
    let facilityInfo = newReportData?.facility

    let reportContent = (() => {
      try {
        return JSON.stringify(
          Object.fromEntries(newReportData.content.map(field => (
            [field.label, markdownToFieldValue(field.value)]
          ))),
          null, 2
        )
      } catch(err) {
        Sentry.captureException(err, {
          extra: {
            context: 'EditReport - markdownToFieldValue conversion',
            reportId: reportId || 'undefined',
            activeChatSessionId: activeChatSession?._id || 'undefined',
            contentFieldsCount: newReportData?.content?.length || 0,
            userId: user?._id || 'undefined',
            patientName: newReportData?.patient?.name || 'undefined',
            facilityName: newReportData?.facility?.name || 'undefined'
          }
        });
        console.error(err)
        showErrorMessage("Invalid markdown value format")
        return null
      }
    })();

    if(reportContent == null) return

    updateReport({
      reportId,
      reportData: {
        current: reportContent,
        patientInfo,
        facilityInfo,
        date: newReportData.date,
      }
    }, {
      onSuccess() {
        gotoPreview()
      },
      onError(error) {
        showErrorMessage(error.message)
      }
    })
  }

  const firstNameInputRef = useRef(null)
  const lastNameInputRef = useRef(null)
  const patientIdInputRef = useRef(null)
  const genderFieldsetRef = useRef(null)
  const ageInputRef = useRef(null)

  /** @param {*} patient */
  function autoFillPatientInfo(patient) {
    if(patient == null) return

    mixpanel.track("Auto Fill Patient Info Edit Report", {
      patientName: patient.patientName,
      patientId: patient.patientId,
      reportId: reportId
    })

    let {firstName, lastName} = splitName(patient.patientName)

    if(firstNameInputRef.current != null) firstNameInputRef.current.value = firstName
    if(lastNameInputRef.current != null) lastNameInputRef.current.value = lastName
    if(patientIdInputRef.current != null) patientIdInputRef.current.value = patient.patientId
    if(genderFieldsetRef.current != null) {
      for(let radio of genderFieldsetRef.current.querySelectorAll("input[type=radio]")) {
        radio.checked = radio.value === patient.gender
      }
    }
    if(ageInputRef.current != null) ageInputRef.current.value = patient.age
  }

  return (
    <div className="chat-report__edit">
      <header>
        <h2>Edit medical Report</h2>
        <p>Add new data or modify existing data to update the report</p>
      </header>

      <form id={formId} className="chat-report__form" onSubmit={handleSubmit}>
        <div className="chat-report__form-fields">
          <fieldset data-transparent>
            <legend className="sr-only">Patient information</legend>

            <div className="form-inline-fieldset">
              <div className="form-field">
                <label
                  className="form-field__label"
                  htmlFor={getScopedId("patient-first-name")}
                >First Name <span className="text-red-500">*</span></label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientName"
                  onSelectPatient={autoFillPatientInfo}
                  filterFn={(search, patient) => {
                    let { firstName = "" } = splitName(patient.patientName)
                    return isCaseInsensitiveSubstring(search, firstName)
                  }}
                >
                  <PatientAutoComplete.Input
                    type="text"
                    className="form-field__input form-field__combobox-input"
                    name="patient-first-name"
                    id={getScopedId("patient-first-name")}
                    defaultValue={splitName(reportData?.patient?.name ?? "").firstName}
                    required
                    ref={firstNameInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>
              <div className="form-field">
                <label
                  className="form-field__label"
                  htmlFor={getScopedId("patient-last-name")}
                >Last Name <span className="text-red-500">*</span></label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientName"
                  onSelectPatient={autoFillPatientInfo}
                  filterFn={(search, patient) => {
                    let { lastName = "" } = splitName(patient.patientName)
                    return isCaseInsensitiveSubstring(search, lastName)
                  }}
                >
                  <PatientAutoComplete.Input
                    type="text"
                    className="form-field__input form-field__combobox-input"
                    name="patient-last-name"
                    id={getScopedId("patient-last-name")}
                    defaultValue={splitName(reportData?.patient?.name ?? "").lastName}
                    required
                    ref={lastNameInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>
            </div>
            <div className="form-inline-fieldset">
              <div className="form-field">
                <label
                  className="form-field__label"
                  htmlFor={getScopedId("patient-age")}
                >Age <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  className="form-field__input"
                  name="patient-age"
                  id={getScopedId("patient-age")}
                  defaultValue={reportData.patient.age}
                  min={0}
                  required
                />
              </div>
              <div className="form-field">
                <label
                  className="form-field__label"
                  htmlFor={getScopedId("patient-id")}
                >Patient ID <span className="text-red-500">*</span></label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientId"
                  onSelectPatient={autoFillPatientInfo}
                >
                  <PatientAutoComplete.Input
                    type="text"
                    className="form-field__input form-field__combobox-input"
                    name="patient-id"
                    id={getScopedId("patient-id")}
                    defaultValue={reportData?.patient?.id}
                    required
                    ref={patientIdInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>
            </div>

            <div className="form-inline-fieldset">
              <div className="form-field">
                <label className="form-field__label" htmlFor="facility-name">Facility Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  className="form-field__input"
                  id="facility-name"
                  name="facility-name"
                  defaultValue={reportData.facility?.name ?? userFacility?.facilityName ?? ""}
                />
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="facility-reporting-doctor">Reporting Doctor <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  className="form-field__input"
                  id="facility-reporting-doctor"
                  name="facility-reporting-doctor"
                  defaultValue={reportData.facility?.reportingDoctor ?? ""}
                />
              </div>
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="facility-report-date">Report Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                required
                className="form-field__input"
                id="report-date"
                name="report-date"
                defaultValue={reportData.date.split("T")[0]}
              />
            </div>

            <fieldset className="form-field" ref={genderFieldsetRef}>
              <legend className="form-field__label">Gender (Sex) <span className="text-red-500">*</span></legend>

              <div className="form-inline-radio-fieldset">
                <div className="form-radio-field">
                  <input 
                    type="radio"
                    required
                    className="form-field__input"
                    name="patient-gender"
                    id={getScopedId("patient-gender-male")}
                    value="Male"
                    defaultChecked={reportData?.patient?.gender === "Male"}
                  />
                  <label htmlFor={getScopedId("patient-gender-male")}>Male</label>
                </div>
                <div className="form-radio-field">
                  <input 
                    type="radio"
                    required
                    className="form-field__input"
                    name="patient-gender"
                    id={getScopedId("patient-gender-female")}
                    value="Female"
                    defaultChecked={reportData?.patient?.gender === "Female"}
                  />
                  <label htmlFor={getScopedId("patient-gender-female")}>Female</label>
                </div>
              </div>
            </fieldset>
          </fieldset>

          {reportData?.modality != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("modality")}
              >Modality</label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"modality"}
                id={getScopedId("modality")}
                defaultValue={reportData?.modality}
                required
                disabled
              />
            </div>
          )}

          {reportData?.technique != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("technique")}
              >Technique</label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"technique"}
                id={getScopedId("technique")}
                defaultValue={reportData?.technique}
                required
                disabled
              />
            </div>
          )}

          {reportData?.examinationReason != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("examination-reason")}
              >Reason for examination</label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"examination-reason"}
                id={getScopedId("examination-reason")}
                defaultValue={reportData?.examinationReason}
                required
                disabled
              />
            </div>
          )}

          {reportData?.clinicalHistory != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("clinical-history")}
              >Relevant clinical history</label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"clinical-history"}
                id={getScopedId("clinical-history")}
                defaultValue={reportData?.clinicalHistory}
                required
                disabled
              />
            </div>
          )}

          {dynamicFields.map(field => (
            <div className="form-field" key={field.label}>
              <div className="form-field__header">
                <label
                  className="form-field__label"
                  data-variant="huge"
                  htmlFor={getScopedId(field.label.toLowerCase().replace(/\s/, "-"))}
                >{field.label}</label>

                {!field.required && (
                  <button
                    type="button"
                    className="form-field__remove-btn"
                    onClick={() => removeField(field.label)}
                  >
                    <MdOutlineDeleteForever />
                    <span>Remove</span>
                  </button>
                )}
              </div>
              <AutoResizingTextarea
                className="form-field__input"
                name={field.label}
                id={getScopedId(field.label.toLowerCase().replace(/\s/, "-"))}
                defaultValue={field.value}
                required={field.required}
              />
            </div>
          ))}

          <div className="add-field-form-body">
            <input name="field" form="add-field-form" required />
            <button 
              type="submit" 
              form="add-field-form"
              className="chat-report__btn chat-report__add-field-btn"
            >
              <FaPlus />
              <span>Add Field</span>
            </button>
          </div>
        </div>

        <div className="chat-report__btn-group chat-report__edit-form-btn-group">
          <button
            type="button"
            className="chat-report__btn"
            data-variant="secondary"
            onClick={gotoPreview}
            disabled={isUpdatingReport}
          >Cancel</button>
          <button
            type="submit"
            className="chat-report__btn"
            data-variant="primary"
            disabled={isUpdatingReport}
          >Save changes</button>
        </div>
      </form>

      <form id="add-field-form" onSubmit={handleAddField}></form>
    </div>
  )
}

/** @param {string} name */
function splitName(name) {
  const parts = name.split(" ")
  if (parts.length === 1) return { firstName: parts[0], lastName: ""}
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  }
}

/**
 * @param {string} search
 * @param {string} value
 */
function isCaseInsensitiveSubstring(search = "", value = "") {
  return value.toLowerCase().includes(search.toLowerCase())
}